import React, { useMemo, useRef, useCallback, useEffect } from 'react';
import * as THREE from 'three';
import earcut from 'earcut';
import { useThree } from '@react-three/fiber';
import { useStudio } from '@/lib/store';
import { CURVED_TEMPLATES } from '@/lib/polyhedra';
import { smoothPerimeter } from '@/lib/smooth';

const RADIUS = 1.25;
const HANDLE_RADIUS = 0.045;
const SEAM_LIFT = 1.002; // seams sit just above panels

const HANDLE_COLORS = {
  none: '#007AFF',
  antipodal: '#10B981',
  mirrorX: '#F59E0B',
  mirrorY: '#EAB308',
  mirrorZ: '#FB923C',
  mirror: '#F59E0B', // legacy alias
  ring: '#EC4899',
  full: '#7C3AED',
};
const SMOOTH_HANDLE_COLOR = '#F59E0B';

function normVec(v) {
  const l = Math.hypot(v[0], v[1], v[2]) || 1;
  return [v[0] / l, v[1] / l, v[2] / l];
}

// Build a BufferGeometry from a pre-densified 3D perimeter (list of unit-sphere
// points). Ear-clipping in 2D projection guarantees every triangle sits inside
// the polygon so panels never fan through their centroid. Curved templates
// additionally subdivide each ear-triangle and re-project every subdivided
// point to the sphere for a smooth dome.
function faceGeometryFromPerimeter(perimeter3D, radius, curved) {
  const geom = new THREE.BufferGeometry();
  const positions = [];
  const normals = [];

  // Centroid direction on the unit sphere — used as the pole for azimuthal
  // projection into 2D so we can run ear-clip in the plane.
  const c = [0, 0, 0];
  perimeter3D.forEach((v) => {
    c[0] += v[0];
    c[1] += v[1];
    c[2] += v[2];
  });
  const cl = Math.hypot(c[0], c[1], c[2]) || 1;
  const cHat = [c[0] / cl, c[1] / cl, c[2] / cl];

  // 2D basis in the plane perpendicular to cHat.
  const up = Math.abs(cHat[0]) < 0.9 ? [1, 0, 0] : [0, 1, 0];
  const dCU = up[0] * cHat[0] + up[1] * cHat[1] + up[2] * cHat[2];
  const uAxis = normVec([up[0] - dCU * cHat[0], up[1] - dCU * cHat[1], up[2] - dCU * cHat[2]]);
  const wAxis = [
    cHat[1] * uAxis[2] - cHat[2] * uAxis[1],
    cHat[2] * uAxis[0] - cHat[0] * uAxis[2],
    cHat[0] * uAxis[1] - cHat[1] * uAxis[0],
  ];

  const project2D = (v) => {
    const d = Math.max(-1, Math.min(1, v[0] * cHat[0] + v[1] * cHat[1] + v[2] * cHat[2]));
    const phi = Math.acos(d);
    const perpX = v[0] - d * cHat[0];
    const perpY = v[1] - d * cHat[1];
    const perpZ = v[2] - d * cHat[2];
    const perpLen = Math.hypot(perpX, perpY, perpZ);
    if (perpLen < 1e-9) return [0, 0];
    const uc = (perpX * uAxis[0] + perpY * uAxis[1] + perpZ * uAxis[2]) / perpLen;
    const vc = (perpX * wAxis[0] + perpY * wAxis[1] + perpZ * wAxis[2]) / perpLen;
    return [phi * uc, phi * vc];
  };

  const poly2D = perimeter3D.map((v) => project2D(v));

  // Ensure CCW winding (positive signed area). Reverse the perimeter below
  // if the projection came out CW, so ear-clip's convex check works.
  let area = 0;
  for (let i = 0; i < poly2D.length; i++) {
    const p = poly2D[i];
    const q = poly2D[(i + 1) % poly2D.length];
    area += p[0] * q[1] - q[0] * p[1];
  }
  const orderedPerim = area >= 0 ? perimeter3D.slice() : perimeter3D.slice().reverse();
  const orderedPoly = area >= 0 ? poly2D : poly2D.slice().reverse();

  const triangles = earClip(orderedPoly);

  const pushTri = (a, b, c) => {
    for (const p of [a, b, c]) {
      positions.push(p[0] * radius, p[1] * radius, p[2] * radius);
      normals.push(p[0], p[1], p[2]);
    }
  };

  const subdivide = (a, b, c, depth) => {
    if (depth === 0) {
      pushTri(a, b, c);
      return;
    }
    const ab = normVec([(a[0] + b[0]) / 2, (a[1] + b[1]) / 2, (a[2] + b[2]) / 2]);
    const bc = normVec([(b[0] + c[0]) / 2, (b[1] + c[1]) / 2, (b[2] + c[2]) / 2]);
    const ca = normVec([(c[0] + a[0]) / 2, (c[1] + a[1]) / 2, (c[2] + a[2]) / 2]);
    subdivide(a, ab, ca, depth - 1);
    subdivide(ab, b, bc, depth - 1);
    subdivide(ca, bc, c, depth - 1);
    subdivide(ab, bc, ca, depth - 1);
  };

  // Depth-3 sphere subdivision (64× per earclip triangle) is what gives
  // every curved template its proper dome — including Triangle / Cube+.
  // We can afford it now because Panel's `faceKey` memoisation means only
  // panels that actually moved during a drag rebuild their geometry, and
  // BufferGeometry.dispose() cleans up old GPU buffers.
  const depth = curved ? 3 : 0;

  for (const [i, j, k] of triangles) {
    const a = orderedPerim[i];
    const b = orderedPerim[j];
    const cv = orderedPerim[k];
    if (curved) subdivide(a, b, cv, depth);
    else pushTri(a, b, cv);
  }

  geom.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geom.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
  return geom;
}

// Robust 2D triangulation via mapbox/earcut. Handles concave shapes,
// self-touching edges, and heavily-deformed panels without leaving the
// redraw-tear artefacts our previous hand-rolled fan fallback produced.
function earClip(poly) {
  const n = poly.length;
  if (n < 3) return [];
  if (n === 3) return [[0, 1, 2]];
  const flat = new Array(n * 2);
  for (let i = 0; i < n; i++) {
    flat[i * 2] = poly[i][0];
    flat[i * 2 + 1] = poly[i][1];
  }
  const idx = earcut(flat);
  const tris = [];
  for (let i = 0; i < idx.length; i += 3) {
    tris.push([idx[i], idx[i + 1], idx[i + 2]]);
  }
  return tris;
}

function SeamLines({ vertices, faces, seamWidth, curved, smoothSet }) {
  const positions = useMemo(() => {
    const arr = [];
    const R = RADIUS * SEAM_LIFT;

    const slerpArc = (va, vb, samples) => {
      const dot = Math.max(-1, Math.min(1, va[0] * vb[0] + va[1] * vb[1] + va[2] * vb[2]));
      const angle = Math.acos(dot);
      const s = Math.sin(angle);
      const pts = [];
      for (let m = 0; m <= samples; m++) {
        const t = m / samples;
        if (Math.abs(s) < 1e-6) {
          pts.push(va);
        } else {
          const w1 = Math.sin((1 - t) * angle) / s;
          const w2 = Math.sin(t * angle) / s;
          pts.push([va[0] * w1 + vb[0] * w2, va[1] * w1 + vb[1] * w2, va[2] * w1 + vb[2] * w2]);
        }
      }
      return pts;
    };

    // 4-sample bezier + 4-sample slerp per edge keeps the seam smooth
    // without exploding line-segment count. With 60 vertices and every
    // corner smoothed this used to produce ~14k segments per drag frame.
    faces.forEach((face) => {
      const perim = smoothPerimeter(face, vertices, smoothSet, 4).map(normVec);
      for (let i = 0; i < perim.length; i++) {
        const a = perim[i];
        const b = perim[(i + 1) % perim.length];
        if (curved) {
          const pts = slerpArc(a, b, 4);
          for (let m = 0; m < pts.length - 1; m++) {
            const p = pts[m], q = pts[m + 1];
            arr.push(p[0] * R, p[1] * R, p[2] * R, q[0] * R, q[1] * R, q[2] * R);
          }
        } else {
          arr.push(a[0] * R, a[1] * R, a[2] * R, b[0] * R, b[1] * R, b[2] * R);
        }
      }
    });
    return new Float32Array(arr);
  }, [vertices, faces, curved, smoothSet]);

  return (
    <lineSegments>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[positions, 3]} />
      </bufferGeometry>
      <lineBasicMaterial color="#0F172A" linewidth={2} transparent opacity={0.85 * (0.5 + seamWidth * 20)} />
    </lineSegments>
  );
}

function Panel({ face, vertices, color, finish, wireframe, index, selected, panelMode, onSelect, onEnterPanelMode, curved, smoothSet }) {
  // Cache each panel's geometry by the flat vertex coordinates of *its own*
  // face (a stable string). Because `vertices` gets a fresh array reference
  // on every drag frame, keying off it directly forces every panel to rebuild
  // even if none of its own vertices moved — this string key lets untouched
  // panels short-circuit useMemo.
  const faceKey = useMemo(() => {
    let k = '';
    for (const i of face) {
      const v = vertices[i];
      k += v[0].toFixed(5) + ',' + v[1].toFixed(5) + ',' + v[2].toFixed(5) + ';';
    }
    return k;
  }, [face, vertices]);
  const perimeter3D = useMemo(() => {
    // 4 bezier samples per smooth corner is visually indistinguishable from
    // 8 but keeps drag frame time low. Sharp corners cost nothing.
    return smoothPerimeter(face, vertices, smoothSet, 4).map(normVec);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [faceKey, smoothSet]);
  const geometry = useMemo(
    () => faceGeometryFromPerimeter(perimeter3D, RADIUS, curved),
    [perimeter3D, curved],
  );
  // Dispose the old BufferGeometry when it's replaced (or on unmount) so we
  // don't leak GPU buffers. Without this, dragging a smoothed vertex for a
  // few seconds fills the WebGL context and stutters/crashes.
  useEffect(() => () => geometry.dispose(), [geometry]);
  const materialProps = useMemo(() => {
    if (finish === 'gloss') return { roughness: 0.15, metalness: 0.1, clearcoat: 0.6 };
    if (finish === 'leather') return { roughness: 0.75, metalness: 0.02 };
    return { roughness: 0.5, metalness: 0.05 };
  }, [finish]);

  const active = panelMode === index;
  return (
    <mesh
      geometry={geometry}
      onClick={(e) => {
        e.stopPropagation();
        onSelect(index);
      }}
      onDoubleClick={(e) => {
        e.stopPropagation();
        onEnterPanelMode(index);
      }}
      castShadow
      receiveShadow
      data-testid={`panel-mesh-${index}`}
    >
      <meshStandardMaterial
        color={color}
        roughness={materialProps.roughness}
        metalness={materialProps.metalness}
        wireframe={wireframe}
        emissive={active ? '#F97316' : selected ? '#007AFF' : '#000000'}
        emissiveIntensity={active ? 0.45 : selected ? 0.25 : 0}
        side={THREE.DoubleSide}
      />
    </mesh>
  );
}

function VertexHandle({ index, position, handleColor, isSmooth }) {
  const mesh = useRef();
  const dragging = useRef(false);
  const dragMoved = useRef(false);
  const { camera, gl } = useThree();
  const moveVertex = useStudio((s) => s.moveVertex);
  const pushHistory = useStudio((s) => s.pushHistory);
  const setDraggingVertex = useStudio((s) => s.setDraggingVertex);
  const toggleSmoothVertex = useStudio((s) => s.toggleSmoothVertex);

  const worldPos = useMemo(
    () => [position[0] * RADIUS, position[1] * RADIUS, position[2] * RADIUS],
    [position],
  );

  const onPointerDown = useCallback(
    (e) => {
      e.stopPropagation();
      dragging.current = true;
      dragMoved.current = false;
      pushHistory();
      setDraggingVertex(true);
      gl.domElement.style.cursor = 'grabbing';
      e.target.setPointerCapture?.(e.pointerId);
    },
    [gl, pushHistory, setDraggingVertex],
  );

  const onPointerUp = useCallback(
    (e) => {
      if (!dragging.current) return;
      dragging.current = false;
      setDraggingVertex(false);
      gl.domElement.style.cursor = 'grab';
      e.target.releasePointerCapture?.(e.pointerId);
    },
    [gl, setDraggingVertex],
  );

  const onPointerMove = useCallback(
    (e) => {
      if (!dragging.current) return;
      e.stopPropagation();
      dragMoved.current = true;
      // Cast a ray from mouse and intersect with sphere of radius RADIUS.
      const rect = gl.domElement.getBoundingClientRect();
      const ndc = new THREE.Vector2(
        ((e.clientX - rect.left) / rect.width) * 2 - 1,
        -((e.clientY - rect.top) / rect.height) * 2 + 1,
      );
      const raycaster = new THREE.Raycaster();
      raycaster.setFromCamera(ndc, camera);
      const sphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), RADIUS);
      const hit = new THREE.Vector3();
      const intersected = raycaster.ray.intersectSphere(sphere, hit);
      if (!intersected) return;
      const n = hit.clone().normalize();
      moveVertex(index, [n.x, n.y, n.z]);
    },
    [camera, gl, index, moveVertex],
  );

  const color = isSmooth ? SMOOTH_HANDLE_COLOR : handleColor;
  return (
    <mesh
      ref={mesh}
      position={worldPos}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      onPointerMove={onPointerMove}
      onDoubleClick={(e) => {
        e.stopPropagation();
        toggleSmoothVertex(index);
      }}
      onPointerOver={() => (gl.domElement.style.cursor = 'grab')}
      onPointerOut={() => {
        if (!dragging.current) gl.domElement.style.cursor = 'auto';
      }}
      data-testid={`vertex-handle-${index}`}
    >
      <sphereGeometry args={[isSmooth ? HANDLE_RADIUS * 1.25 : HANDLE_RADIUS, 20, 20]} />
      <meshStandardMaterial
        color={color}
        emissive={color}
        emissiveIntensity={isSmooth ? 0.7 : 0.4}
        roughness={0.3}
      />
    </mesh>
  );
}

// While a panel is in "slide" mode, this controller listens for pointer
// events at the canvas level, ray-casts to the sphere, and rotates every
// vertex on the selected face by the delta between successive hits.
function PanelDragController() {
  const { camera, gl } = useThree();
  const panelDragMode = useStudio((s) => s.panelDragMode);
  const panelManipMode = useStudio((s) => s.panelManipMode);
  const setPanelDragMode = useStudio((s) => s.setPanelDragMode);
  const setDraggingPanel = useStudio((s) => s.setDraggingPanel);
  const movePanel = useStudio((s) => s.movePanel);
  const pushHistory = useStudio((s) => s.pushHistory);
  const lastDir = useRef(null);

  useEffect(() => {
    if (panelDragMode == null) return;
    // Drag-to-slide is only active in Move mode. Scale & Rotate use buttons
    // so the user can nudge with a predictable delta.
    if (panelManipMode !== 'move') return;
    const canvas = gl.domElement;
    const raycaster = new THREE.Raycaster();
    const sphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), RADIUS);

    const hitDir = (evt) => {
      const rect = canvas.getBoundingClientRect();
      const ndc = new THREE.Vector2(
        ((evt.clientX - rect.left) / rect.width) * 2 - 1,
        -((evt.clientY - rect.top) / rect.height) * 2 + 1,
      );
      raycaster.setFromCamera(ndc, camera);
      const hit = new THREE.Vector3();
      if (!raycaster.ray.intersectSphere(sphere, hit)) return null;
      const n = hit.clone().normalize();
      return [n.x, n.y, n.z];
    };

    const onDown = (evt) => {
      const d = hitDir(evt);
      if (!d) return;
      lastDir.current = d;
      pushHistory();
      setDraggingPanel(true);
      canvas.style.cursor = 'grabbing';
    };
    const onMove = (evt) => {
      if (!lastDir.current) return;
      const d = hitDir(evt);
      if (!d) return;
      movePanel(panelDragMode, lastDir.current, d);
      lastDir.current = d;
    };
    const onUp = () => {
      lastDir.current = null;
      setDraggingPanel(false);
      canvas.style.cursor = 'grab';
    };
    const onKey = (evt) => {
      if (evt.key === 'Escape') setPanelDragMode(null);
    };

    canvas.style.cursor = 'grab';
    canvas.addEventListener('pointerdown', onDown);
    canvas.addEventListener('pointermove', onMove);
    canvas.addEventListener('pointerup', onUp);
    canvas.addEventListener('pointerleave', onUp);
    window.addEventListener('keydown', onKey);
    return () => {
      canvas.style.cursor = 'auto';
      canvas.removeEventListener('pointerdown', onDown);
      canvas.removeEventListener('pointermove', onMove);
      canvas.removeEventListener('pointerup', onUp);
      canvas.removeEventListener('pointerleave', onUp);
      window.removeEventListener('keydown', onKey);
    };
  }, [panelDragMode, panelManipMode, camera, gl, movePanel, pushHistory, setDraggingPanel, setPanelDragMode]);

  return null;
}

export default function PanelBall() {
  const vertices = useStudio((s) => s.vertices);
  const faces = useStudio((s) => s.faces);
  const colors = useStudio((s) => s.colors);
  const seamWidth = useStudio((s) => s.seamWidth);
  const finish = useStudio((s) => s.finish);
  const wireframe = useStudio((s) => s.wireframe);
  const showHandles = useStudio((s) => s.showHandles);
  const symmetryMode = useStudio((s) => s.symmetryMode);
  const selectedPanel = useStudio((s) => s.selectedPanel);
  const setSelectedPanel = useStudio((s) => s.setSelectedPanel);
  const panelDragMode = useStudio((s) => s.panelDragMode);
  const setPanelDragMode = useStudio((s) => s.setPanelDragMode);
  const smoothVertices = useStudio((s) => s.smoothVertices);
  const templateId = useStudio((s) => s.templateId);
  const curved = CURVED_TEMPLATES.has(templateId);

  const smoothSet = useMemo(() => new Set(smoothVertices || []), [smoothVertices]);

  return (
    <group data-testid="panel-ball-group">
      {faces.map((face, i) => (
        <Panel
          key={`p-${i}`}
          face={face}
          vertices={vertices}
          color={colors[i] || '#F9FAFB'}
          finish={finish}
          wireframe={wireframe}
          index={i}
          selected={selectedPanel === i}
          panelMode={panelDragMode}
          onSelect={setSelectedPanel}
          onEnterPanelMode={setPanelDragMode}
          curved={curved}
          smoothSet={smoothSet}
        />
      ))}
      <SeamLines vertices={vertices} faces={faces} seamWidth={seamWidth} curved={curved} smoothSet={smoothSet} />
      {showHandles &&
        vertices.map((v, i) => (
          <VertexHandle
            key={`v-${i}`}
            index={i}
            position={v}
            handleColor={HANDLE_COLORS[symmetryMode] || HANDLE_COLORS.none}
            isSmooth={smoothSet.has(i)}
          />
        ))}
      <PanelDragController />
    </group>
  );
}
